import { toast } from '@montugroup/design-system';
import { Logger } from '@montugroup/utilities';
import { create } from 'zustand';
import { shallow } from 'zustand/shallow';
const logger = new Logger('usePersistedFileGenerationRequestsStore');
export const FILE_GENERATION_REQUESTS_STORAGE_KEY = 'fileGenerationRequests';
const DEFAULT_VALUE = {};
const INITIAL_ACTIONS = {
    createFileRequest: () => { },
    deleteFileRequest: () => { },
    updateFileRequest: () => { },
};
export const isPollableRequest = (request) => {
    if (request.status === 'Pending') {
        return request.serverId;
    }
    // NOTE: We need to make at least 1 request for 'Done' requests stored locally on the first request of the page, as a new signed file URL is required every ~6 hours
    if (request.status === 'Done' && !request.downloadStatus) {
        return request.serverId;
    }
    return undefined;
};
const getToastConfig = ({ id, status, downloadStatus, store, }) => {
    const toastConfigs = {
        Pending: {
            title: downloadStatus ? 'Files are downloading' : 'Files are generating',
            message: downloadStatus
                ? 'Download will continue in the background. You can leave the page.'
                : 'Generation will continue in the background. You can leave the page.',
            toastId: id,
            type: 'info',
            position: 'top-right',
            isLoading: true,
            autoClose: false,
            closeOnClick: false,
            closeButton: false,
            onClose: () => { },
        },
        Failed: {
            title: 'Error',
            message: downloadStatus ? 'File download failed' : 'File generation failed',
            toastId: id,
            type: 'error',
            position: 'top-right',
            isLoading: false,
            autoClose: 10_000,
            closeOnClick: false,
            closeButton: true,
            onClose: () => store().actions.deleteFileRequest(id),
        },
        Done: {
            title: 'Download successful',
            message: 'Check your downloads folder',
            toastId: id,
            type: 'success',
            position: 'top-right',
            isLoading: false,
            autoClose: 10_000,
            closeOnClick: false,
            closeButton: true,
            onClose: () => store().actions.deleteFileRequest(id),
        },
    };
    return toastConfigs[downloadStatus || status];
};
const showToast = ({ fileRequest, store }) => {
    const isToastActiveForRequest = toast.isActive(fileRequest.serverId);
    logger.info('showToast', {
        isToastActiveForRequest,
        fileRequest,
    });
    if (!isToastActiveForRequest) {
        const { type, message, ...rest } = getToastConfig({
            id: fileRequest.serverId,
            status: fileRequest.status,
            downloadStatus: fileRequest.downloadStatus,
            store,
        });
        toast[type](message, rest);
        // TODO: Remove the following line after supporting `title` on initial toast.<type> as it's currently supported in .update
        toast.update(fileRequest.serverId, getToastConfig({
            id: fileRequest.serverId,
            status: fileRequest.status,
            downloadStatus: fileRequest.downloadStatus,
            store,
        }));
    }
    else {
        toast.update(fileRequest.serverId, getToastConfig({
            id: fileRequest.serverId,
            status: fileRequest.status,
            downloadStatus: fileRequest.downloadStatus,
            store,
        }));
    }
};
const removeInvalidOrFinishedRequests = (localRequests) => {
    const validRequests = {};
    Object.entries(localRequests).forEach(([id, localRequest]) => {
        if (isPollableRequest(localRequest)) {
            logger.warn('removeInvalidOrFinishedRequests::adding', {
                id,
                localRequest,
            });
            validRequests[id] = localRequest;
        }
        else {
            logger.warn('removeInvalidOrFinishedRequests::removing', {
                id,
                localRequest,
            });
        }
    });
    return validRequests;
};
function getInitialState() {
    try {
        const stored = JSON.parse(localStorage.getItem(FILE_GENERATION_REQUESTS_STORAGE_KEY) || '{}');
        const fileGenerationRequests = removeInvalidOrFinishedRequests(stored);
        localStorage.setItem(FILE_GENERATION_REQUESTS_STORAGE_KEY, JSON.stringify(fileGenerationRequests));
        if (fileGenerationRequests) {
            return {
                fileGenerationRequests,
                actions: INITIAL_ACTIONS,
            };
        }
    }
    catch (error) {
        logger.error('getInitialState', {
            error,
        });
    }
    return {
        fileGenerationRequests: DEFAULT_VALUE,
        actions: INITIAL_ACTIONS,
    };
}
export const usePersistedFileGenerationRequestsStore = create((set, get) => {
    const initial = getInitialState();
    const persist = (newState) => {
        const { actions: _, fileGenerationRequests } = { ...get(), ...newState };
        try {
            logger.info('persist', {
                fileGenerationRequests,
            });
            localStorage.setItem(FILE_GENERATION_REQUESTS_STORAGE_KEY, JSON.stringify(fileGenerationRequests));
        }
        catch (error) {
            logger.error('persist', {
                error,
                fileGenerationRequests,
            });
        }
    };
    const store = {
        ...initial,
        actions: {
            createFileRequest: (fileRequest) => {
                const storeState = get();
                const newState = {
                    ...storeState.fileGenerationRequests,
                    [fileRequest.serverId]: fileRequest,
                };
                logger.info('createFileRequest', {
                    fileRequest,
                });
                set({ fileGenerationRequests: newState });
                persist({ fileGenerationRequests: newState });
                showToast({ fileRequest, store: get });
            },
            updateFileRequest: (fileRequest) => {
                const storeState = get();
                const oldFileRequest = { ...storeState.fileGenerationRequests[fileRequest.serverId] };
                const storedFileRequest = JSON.parse(localStorage.getItem(FILE_GENERATION_REQUESTS_STORAGE_KEY) || '{}')[fileRequest.serverId];
                const combinedFileRequest = { ...oldFileRequest, ...storedFileRequest, ...fileRequest };
                const newState = {
                    ...storeState.fileGenerationRequests,
                    [fileRequest.serverId]: combinedFileRequest,
                };
                const hasChanges = !shallow(oldFileRequest, combinedFileRequest);
                // If there is no store change skip this update to prevent re-renders
                if (hasChanges) {
                    logger.info('updateFileRequest::updatingStore', {
                        oldFileRequest,
                        fileRequest,
                        combinedFileRequest,
                    });
                    set({ fileGenerationRequests: newState });
                    persist({ fileGenerationRequests: newState });
                }
                // Only trigger to show the toast if it's currently not showing, or there are store changes
                if (!toast.isActive(fileRequest.serverId) || hasChanges) {
                    showToast({ fileRequest: combinedFileRequest, store: get });
                }
            },
            deleteFileRequest: (id) => {
                const storeState = get();
                const newState = {
                    ...storeState.fileGenerationRequests,
                };
                logger.info('deleteFileRequest', {
                    id,
                    oldFileRequest: storeState.fileGenerationRequests[id],
                });
                delete newState[id];
                set({ fileGenerationRequests: newState });
                persist({ fileGenerationRequests: newState });
            },
        },
    };
    return store;
});
//# sourceMappingURL=data:application/json;base64,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